import { useController, useFormContext } from 'react-hook-form';
import { CheckboxWithLabel, SelectInputField, TextInputField } from '@shared/components';
import { useCampaignTFunction } from '@retail/campaign/i18n';
import { InputDatePicker } from '@retail/components';
import { Grid } from '@mui/material';
import { useMemo } from 'react';
import { CampaignType } from '@retail/campaign/types';
import { Brand } from '@retail/auth/types';
import { BrandCampaignSchema, MemberCampaignSchema } from './campaignSchema';

export const CampaignNameField = () => {
  const t = useCampaignTFunction();
  const { control } = useFormContext<BrandCampaignSchema | MemberCampaignSchema>();
  const controller = useController({ control, name: 'campaignName' });

  return (
    <TextInputField
      {...controller.field}
      error={controller.fieldState.error?.message}
      label={t('campaign.columns.name')}
      required
      size="medium"
      fullWidth
      margin="none"
      maxContentLength={40}
    />
  );
};

export const CampaignDescriptionField = () => {
  const t = useCampaignTFunction();
  const { control } = useFormContext<BrandCampaignSchema | MemberCampaignSchema>();
  const controller = useController({ control, name: 'description' });

  return (
    <TextInputField
      {...controller.field}
      error={controller.fieldState.error?.message}
      label={t('campaign.columns.description')}
      size="medium"
      rows={3}
      multiline
      fullWidth
      maxContentLength={200}
    />
  );
};

export const CampaignDateFields = () => {
  const t = useCampaignTFunction();
  const today = new Date();
  const { control } = useFormContext<BrandCampaignSchema | MemberCampaignSchema>();

  const validFromController = useController({ control, name: 'validFrom' });
  const validToController = useController({ control, name: 'validTo' });
  const purchasePeriodFromController = useController({ control, name: 'purchasePeriodFrom' });
  const purchasePeriodToController = useController({ control, name: 'purchasePeriodTo' });

  return (
    <Grid container spacing={2} width="100%" mb={2} mt={1}>
      <Grid size={{ xs: 6 }}>
        <InputDatePicker
          {...validFromController.field}
          error={validFromController.fieldState.error?.message}
          label={t('campaign.columns.validFrom')}
          minDate={today}
          margin="none"
          size="small"
          labelDirection="row"
          required
        />
      </Grid>

      <Grid size={{ xs: 6 }}>
        <InputDatePicker
          {...validToController.field}
          error={validToController.fieldState.error?.message}
          label={t('campaign.columns.validTo')}
          minDate={validFromController.field.value}
          margin="none"
          size="small"
          labelDirection="row"
          required
        />
      </Grid>

      <Grid size={{ xs: 6 }}>
        <InputDatePicker
          {...purchasePeriodFromController.field}
          error={purchasePeriodFromController.fieldState.error?.message}
          label={t('campaign.columns.purchasePeriodFrom')}
          margin="none"
          size="small"
          labelDirection="row"
          required
        />
      </Grid>

      <Grid size={{ xs: 6 }}>
        <InputDatePicker
          {...purchasePeriodToController.field}
          error={purchasePeriodToController.fieldState.error?.message}
          label={t('campaign.columns.purchasePeriodTo')}
          margin="none"
          size="small"
          labelDirection="row"
          required
        />
      </Grid>
    </Grid>
  );
};

export const CampaignMandatoryField = () => {
  const t = useCampaignTFunction();
  const { control } = useFormContext<BrandCampaignSchema>();
  const mandatoryController = useController({ control, name: 'mandatory' });

  return (
    <CheckboxWithLabel
      {...mandatoryController.field}
      label={t('campaign.columns.mandatoryLong')}
      fullWidth
      margin="none"
      size="medium"
    />
  );
};

export const CampaignTypeField = () => {
  const t = useCampaignTFunction();
  const { control } = useFormContext<BrandCampaignSchema>();
  const campaignTypeController = useController({ control, name: 'campaignType' });

  const campaignTypeItems: { name: string; value: CampaignType }[] = useMemo(() => {
    return [
      { name: t('campaign.central'), value: 'CENTRAL' },
      { name: t('campaign.supplier'), value: 'SUPPLIER' },
      { name: t('campaign.optional'), value: 'OPTIONAL' },
    ];
  }, [t]);

  return (
    <SelectInputField
      {...campaignTypeController.field}
      label={t('campaign.columns.campaignType')}
      error={campaignTypeController.fieldState.error?.message}
      menuItems={campaignTypeItems}
      fullWidth
      required
    />
  );
};

interface CampaignBrandsFieldProps {
  availableBrands: Brand[];
}

export const CampaignBrandsField = ({ availableBrands }: CampaignBrandsFieldProps) => {
  const t = useCampaignTFunction();
  const { control } = useFormContext<BrandCampaignSchema>();

  const brandsController = useController({ control, name: 'brands' });

  const brandOptions = useMemo(
    () =>
      availableBrands.map((brand) => ({
        name: brand.name,
        value: brand.brandId,
      })),
    [availableBrands]
  );

  return (
    <SelectInputField
      {...brandsController.field}
      label={t('campaign.columns.brands')}
      error={brandsController.fieldState.error?.message}
      menuItems={brandOptions}
      menuItemHasSpaceBetween={false}
      tooltipText={t('campaign.messages.brandInfo')}
      multiple
      fullWidth
    />
  );
};
